import {
  Button,
  Card,
  CardHeader,
  CurrencyWrapper,
  Text,
} from "@/components/ui";
import { MatterTrustCheckContextMenu } from "@/components/features/matter/context-menus/MatterTrustCheckContextMenu";
import type { TrustCheckViewModel } from "@/hooks/matter-view-models/use-trust-checks-view-model";
import { useGlobalModal } from "@/hooks/useGlobalModal";
import type { Matter } from "@/lib/types";
import { cn } from "@/lib/utils/cn";
import { formatDate } from "@/lib/utils/format";
import { Pencil } from "lucide-react";

interface TrustCheckCardProps {
  trustCheck: TrustCheckViewModel;
  matter: Matter;
  className?: string;
  showConnections?: boolean;
}

export const TrustCheckCard = ({
  trustCheck,
  matter,
  className,
  showConnections = false,
}: TrustCheckCardProps) => {
  const { openTrustCheckModal } = useGlobalModal();
  const handleEdit = () => {
    openTrustCheckModal({
      matterId: trustCheck.matterId,
      trustCheckId: trustCheck.id,
      trustCheckType: trustCheck.type,
      editing: true,
    });
  };
  return (
    <MatterTrustCheckContextMenu
      matter={matter}
      onEdit={handleEdit}
      trustCheck={trustCheck}
      trigger={
        <Card
          className={cn(
            "hover:bg-muted/50 w-full cursor-pointer transition-colors",
            className,
          )}
          onClick={handleEdit}
        >
          <CardHeader className="space-y-1 p-0 px-6 py-4">
            <div className="flex items-center justify-between gap-y-2">
              <div className="flex flex-col gap-x-2">
                <div className="flex min-w-0 flex-1 flex-col items-start gap-x-2">
                  <Text
                    variant="body"
                    color="primary"
                    className="flex min-w-0 items-center gap-x-2 truncate font-semibold"
                  >
                    {trustCheck.type}
                    <span className="mx-0.5">|</span>
                    <span
                      className={cn(
                        trustCheck.type === "Deposit"
                          ? "text-green-600"
                          : "text-red-600",
                      )}
                    >
                      <CurrencyWrapper value={trustCheck.amount ?? 0} />
                    </span>
                    <Text variant="caption" color="muted" as="span">
                      (Ref #{trustCheck.referenceNumber})
                    </Text>
                  </Text>
                  {showConnections && trustCheck.connections.length > 0 && (
                    <Text
                      variant="subHeading"
                      color="primary"
                      className="whitespace-nowrap"
                    >
                      {trustCheck.connections
                        .map((connection) => {
                          if (connection.lienId) {
                            return `Lien: ${connection.matterLien?.label}`;
                          } else if (connection.expenseId) {
                            return `Expense: ${connection.matterExpense?.label}`;
                          } else if (connection.settlementOfferId) {
                            const offerDateSent =
                              connection.matterSettlementOffer?.dateReceived ??
                              (
                                connection.matterSettlementOffer as
                                  | {
                                      matterSettlementOfferDateReceived?:
                                        | string
                                        | null;
                                    }
                                  | undefined
                              )?.matterSettlementOfferDateReceived;
                            return offerDateSent
                              ? `Settlement: ${formatDate(offerDateSent)}`
                              : undefined;
                          } else if (connection.settlementCounterId) {
                            const counterDateSent =
                              connection.matterSettlementCounter?.dateSent;
                            return counterDateSent
                              ? `Settlement Counter: ${formatDate(counterDateSent)}`
                              : "Settlement Counter";
                          } else if (connection.medicalTreatmentBillId) {
                            return `Medical Treatment Bill: Requested ${formatDate(
                              connection.medicalTreatmentBills
                                ?.recordRequestDate ?? "",
                            )}`;
                          }
                        })
                        .join(", ")}
                    </Text>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-x-2">
                <Text
                  variant="subHeading"
                  color="primary"
                  className="whitespace-nowrap"
                >
                  {formatDate(trustCheck.dateReceived)}
                </Text>
                <Button
                  type="button"
                  data-testid={`edit-trust-check-${trustCheck.type.toLowerCase()}-button`}
                  variant="ghost"
                  size="icon"
                  onClick={(event) => {
                    event.stopPropagation();
                    handleEdit();
                  }}
                  className="text-muted-foreground hover:text-foreground hover:bg-muted/70 focus-visible:bg-muted/70 h-8 w-8 shrink-0"
                >
                  <Pencil className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </CardHeader>
        </Card>
      }
    />
  );
};
